import { AlertType, Season, SessionTimes } from '@types';
import { AlertModal, Card, PageHeader, SectionHeader } from '_atoms';
import { AdminService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdAdd,
  MdCelebration,
  MdConfirmationNumber,
  MdDelete,
  MdEvent,
  MdPeople,
  MdSave,
  MdSchedule,
} from 'react-icons/md';

const SeasonSettings: React.FC = () => {
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSessionEditMode, setIsSessionEditMode] = useState(false);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [isLoadingSeason, setIsLoadingSeason] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingAddSession, setIsLoadingAddSession] = useState(false);
  const [, setIsLoadingRemoveSession] = useState(false);
  const [removingSessionId, setRemovingSessionId] = useState<string | null>(
    null,
  );
  const [isLoadingToggleTicketPurchases, setIsLoadingToggleTicketPurchases] =
    useState(false);
  const [
    isLoadingToggleSeasonPassPurchases,
    setIsLoadingToggleSeasonPassPurchases,
  ] = useState(false);
  const [
    isLoadingTogglePartyPackagePurchases,
    setIsLoadingTogglePartyPackagePurchases,
  ] = useState(false);
  const [
    isLoadingToggleSkatingLessonPurchases,
    setIsLoadingToggleSkatingLessonPurchases,
  ] = useState(false);
  const [
    isLoadingToggleHockeyLessonPurchases,
    setIsLoadingToggleHockeyLessonPurchases,
  ] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [dateError, setDateError] = useState<string>('');
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const validateDates = (startDate: string, endDate: string): boolean => {
    if (!startDate || !endDate) return true;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start >= end) {
      setDateError('Season Start Date must come before Season End Date');
      return false;
    }

    setDateError('');
    return true;
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setSettings((prev: Season) => ({
      ...prev,
      [field]: value,
    }));

    // Validate dates when both are present
    if (field === 'startDate') {
      validateDates(value, settings.endDate);
    } else {
      validateDates(settings.startDate, value);
    }
  };

  const handleSessionTimeChange = (
    id: string,
    field: keyof SessionTimes,
    value: string | number,
  ) => {
    setSettings((prev: Season) => ({
      ...prev,
      sessionTimes: prev.sessionTimes.map((session: SessionTimes) =>
        session.id === id ? { ...session, [field]: value } : session,
      ),
    }));
  };

  const addSessionTime = useCallback(async () => {
    setIsLoadingAddSession(true);

    try {
      // Simulate a small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));

      // Get the last session's start time
      const lastSession =
        settings.sessionTimes[settings.sessionTimes.length - 1];
      let nextStartTime = '10:00'; // Default fallback

      if (lastSession) {
        // Parse the last start time and add 1 hour
        const [hours, minutes] = lastSession.startTime.split(':').map(Number);
        const nextHour = hours + 1;

        // Handle 24-hour format (if next hour is 24, it becomes 00)
        const adjustedHour = nextHour >= 24 ? nextHour - 24 : nextHour;
        nextStartTime = `${adjustedHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }

      const newSession: SessionTimes = {
        id: Date.now().toString(),
        seasonId: settings.id,
        startTime: nextStartTime,
        maxCapacity: 160,
        partiesLimit: 0,
      };

      console.log(newSession);
      setSettings((prev: Season) => ({
        ...prev,
        sessionTimes: [...prev.sessionTimes, newSession],
      }));
    } catch (error) {
      console.error('Error adding session time:', error);
      showAlert(
        'error',
        'Error',
        'Error adding session time. Please try again.',
      );
    } finally {
      setIsLoadingAddSession(false);
    }
  }, [settings.sessionTimes, settings.id, showAlert]);

  const removeSessionTime = useCallback(
    async (id: string) => {
      setIsLoadingRemoveSession(true);
      setRemovingSessionId(id);

      try {
        // Simulate a small delay to show loading state
        await new Promise(resolve => setTimeout(resolve, 300));

        setSettings((prev: Season) => ({
          ...prev,
          sessionTimes: prev.sessionTimes.filter(
            (session: SessionTimes) => session.id !== id,
          ),
        }));
      } catch (error) {
        console.error('Error removing session time:', error);
        showAlert(
          'error',
          'Error',
          'Error removing session time. Please try again.',
        );
      } finally {
        setIsLoadingRemoveSession(false);
        setRemovingSessionId(null);
      }
    },
    [showAlert],
  );

  const handleSaveSeasonDates = async () => {
    // Validate dates before submission
    if (!validateDates(settings.startDate, settings.endDate)) {
      return;
    }

    setIsLoadingSeason(true);

    try {
      const payload = {
        startDate: settings.startDate,
        endDate: settings.endDate,
      };

      await AdminService.createSeasonDuration(payload);
      showAlert('success', 'Success', 'Season dates saved successfully!');
    } catch (error) {
      console.error('Error saving season dates:', error);
      showAlert(
        'error',
        'Error',
        'Error saving season dates. Please try again.',
      );
    } finally {
      setIsLoadingSeason(false);
    }
  };

  const handleUpdateSessionTimes = async () => {
    setIsLoadingSessions(true);

    try {
      // Filter the payload to only include the fields expected by SessionTimes type
      const payload = settings.sessionTimes.map(session => ({
        id: session.id.toString(),
        seasonId: session.seasonId,
        startTime: session.startTime,
        maxCapacity: session.maxCapacity,
        partiesLimit: session.partiesLimit,
      }));

      await AdminService.createSessionTimes(payload);
      showAlert(
        'success',
        'Success',
        'Daily session times updated successfully!',
      );
    } catch (error) {
      console.error('Error updating session times:', error);
      showAlert(
        'error',
        'Error',
        'Error updating session times. Please try again.',
      );
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Load current season details on page load
  useEffect(() => {
    const loadCurrentSeason = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await AdminService.getCurrentSeasonDetails()) as unknown as Season;

        if (
          responseData &&
          (responseData.startDate ||
            responseData.endDate ||
            responseData.sessionTimes.length > 0)
        ) {
          setSettings(responseData);
          setHasExistingSettings(true);
          setIsEditMode(false);
        } else {
          setHasExistingSettings(false);
          setIsEditMode(true);
          showAlert(
            'info',
            'Info',
            'There are currently no active season settings.',
          );
        }
      } catch (error) {
        console.error('Error loading current season:', error);
        showAlert('error', 'Error', 'Failed to load current season settings.');
        setHasExistingSettings(false);
        setIsEditMode(true);
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadCurrentSeason();
  }, [showAlert]);

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
  };

  const handleSessionEditToggle = () => {
    setIsSessionEditMode(!isSessionEditMode);
  };

  const handleToggleTicketPurchases = useCallback(async () => {
    setIsLoadingToggleTicketPurchases(true);

    try {
      await AdminService.toggleTicketPurchases();
      // Refresh the current season data to get updated status
      const responseData =
        (await AdminService.getCurrentSeasonDetails()) as unknown as Season;
      setSettings(responseData);
      showAlert(
        'success',
        'Success',
        `Ticket purchases ${responseData.isTicketPurchasesEnabled ? 'enabled' : 'disabled'} successfully!`,
      );
    } catch (error) {
      console.error('Error toggling ticket purchases:', error);
      showAlert(
        'error',
        'Error',
        'Error updating ticket purchase status. Please try again.',
      );
    } finally {
      setIsLoadingToggleTicketPurchases(false);
    }
  }, [showAlert]);

  const handleToggleSeasonPassPurchases = useCallback(async () => {
    setIsLoadingToggleSeasonPassPurchases(true);

    try {
      await AdminService.toggleSeasonPassPurchases();
      // Refresh the current season data to get updated status
      const responseData =
        (await AdminService.getCurrentSeasonDetails()) as unknown as Season;
      setSettings(responseData);
      showAlert(
        'success',
        'Success',
        `Season pass purchases ${responseData.isSeasonPassPurchasesEnabled ? 'enabled' : 'disabled'} successfully!`,
      );
    } catch (error) {
      console.error('Error toggling season pass purchases:', error);
      showAlert(
        'error',
        'Error',
        'Error updating season pass purchase status. Please try again.',
      );
    } finally {
      setIsLoadingToggleSeasonPassPurchases(false);
    }
  }, [showAlert]);

  const handleTogglePartyPackagePurchases = useCallback(async () => {
    setIsLoadingTogglePartyPackagePurchases(true);

    try {
      await AdminService.togglePartyPackagePurchases();
      // Refresh the current season data to get updated status
      const responseData =
        (await AdminService.getCurrentSeasonDetails()) as unknown as Season;
      setSettings(responseData);
      showAlert(
        'success',
        'Success',
        `Party package purchases ${responseData.isPartyPackagePurchasesEnabled ? 'enabled' : 'disabled'} successfully!`,
      );
    } catch (error) {
      console.error('Error toggling party package purchases:', error);
      showAlert(
        'error',
        'Error',
        'Error updating party package purchase status. Please try again.',
      );
    } finally {
      setIsLoadingTogglePartyPackagePurchases(false);
    }
  }, [showAlert]);

  const handleToggleSkatingLessonPurchases = useCallback(async () => {
    setIsLoadingToggleSkatingLessonPurchases(true);

    try {
      await AdminService.toggleSkatingLessonPurchases();
      // Refresh the current season data to get updated status
      const responseData =
        (await AdminService.getCurrentSeasonDetails()) as unknown as Season;
      setSettings(responseData);
      showAlert(
        'success',
        'Success',
        `Skating lesson purchases ${responseData.isSkatingLessonPurchasesEnabled ? 'enabled' : 'disabled'} successfully!`,
      );
    } catch (error) {
      console.error('Error toggling skating lesson purchases:', error);
      showAlert(
        'error',
        'Error',
        'Error updating skating lesson purchase status. Please try again.',
      );
    } finally {
      setIsLoadingToggleSkatingLessonPurchases(false);
    }
  }, [showAlert]);

  const handleToggleHockeyLessonPurchases = useCallback(async () => {
    setIsLoadingToggleHockeyLessonPurchases(true);

    try {
      await AdminService.toggleHockeyLessonPurchases();
      // Refresh the current season data to get updated status
      const responseData =
        (await AdminService.getCurrentSeasonDetails()) as unknown as Season;
      setSettings(responseData);
      showAlert(
        'success',
        'Success',
        `Hockey lesson purchases ${responseData.isHockeyLessonPurchasesEnabled ? 'enabled' : 'disabled'} successfully!`,
      );
    } catch (error) {
      console.error('Error toggling hockey lesson purchases:', error);
      showAlert(
        'error',
        'Error',
        'Error updating hockey lesson purchase status. Please try again.',
      );
    } finally {
      setIsLoadingToggleHockeyLessonPurchases(false);
    }
  }, [showAlert]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdEvent}
        title="Season Settings"
        description="Configure season dates and session times for the skating rink."
      />

      {/* Main Form */}
      {isLoadingInitial ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading season settings...</p>
            </div>
          </div>
        </Card>
      ) : (
        <form className="space-y-6">
          {/* Season Dates Section */}
          <Card className="flex flex-col">
            <SectionHeader
              title="Season Dates"
              icon={MdEvent}
              showEditButton={hasExistingSettings}
              isEditMode={isEditMode}
              onEditToggle={handleEditToggle}
            />

            {hasExistingSettings && !isEditMode ? (
              // Read-only view
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Season Start Date
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900">
                    {settings.startDate
                      ? new Date(settings.startDate).toLocaleDateString()
                      : 'Not set'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Season End Date
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900">
                    {settings.endDate
                      ? new Date(settings.endDate).toLocaleDateString()
                      : 'Not set'}
                  </div>
                </div>
              </div>
            ) : (
              // Edit view
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="startDate"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Season Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      value={settings.startDate}
                      onChange={e =>
                        handleDateChange('startDate', e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        dateError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="endDate"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Season End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      value={settings.endDate}
                      onChange={e =>
                        handleDateChange('endDate', e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        dateError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                  </div>
                </div>

                {dateError && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600 flex items-center">
                      <span className="mr-2">⚠️</span>
                      {dateError}
                    </p>
                  </div>
                )}

                {/* Save Season Dates Button */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleSaveSeasonDates}
                    disabled={
                      isLoadingSeason ||
                      !settings.startDate ||
                      !settings.endDate ||
                      !!dateError
                    }
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <MdSave className="mr-2" />
                    {isLoadingSeason ? 'Saving...' : 'Save Season Dates'}
                  </button>
                </div>
              </>
            )}
          </Card>

          {/* Enable Ticket Purchases Section */}
          {hasExistingSettings && (
            <Card className="flex flex-col">
              <SectionHeader
                title="Enable Ticket Purchases"
                icon={MdConfirmationNumber}
              />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Ticket Purchase Status
                  </p>
                  <p className="text-xs text-gray-500">
                    {settings.isTicketPurchasesEnabled
                      ? 'Ticket purchases are currently enabled'
                      : 'Ticket purchases are currently disabled'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleTicketPurchases}
                  disabled={isLoadingToggleTicketPurchases}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.isTicketPurchasesEnabled
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`}
                >
                  {isLoadingToggleTicketPurchases ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mx-1"></div>
                  ) : (
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        settings.isTicketPurchasesEnabled
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  )}
                </button>
              </div>
            </Card>
          )}

          {/* Enable Season Pass Purchases Section */}
          {hasExistingSettings && (
            <Card className="flex flex-col">
              <SectionHeader
                title="Enable Season Pass Purchases"
                icon={MdConfirmationNumber}
              />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Season Pass Purchase Status
                  </p>
                  <p className="text-xs text-gray-500">
                    {settings.isSeasonPassPurchasesEnabled
                      ? 'Season pass purchases are currently enabled'
                      : 'Season pass purchases are currently disabled'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleSeasonPassPurchases}
                  disabled={isLoadingToggleSeasonPassPurchases}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.isSeasonPassPurchasesEnabled
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`}
                >
                  {isLoadingToggleSeasonPassPurchases ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mx-1"></div>
                  ) : (
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        settings.isSeasonPassPurchasesEnabled
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  )}
                </button>
              </div>
            </Card>
          )}

          {/* Enable Party Package Purchases Section */}
          {hasExistingSettings && (
            <Card className="flex flex-col">
              <SectionHeader
                title="Enable Party Package Purchases"
                icon={MdPeople}
              />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Party Package Purchase Status
                  </p>
                  <p className="text-xs text-gray-500">
                    {settings.isPartyPackagePurchasesEnabled
                      ? 'Party package purchases are currently enabled'
                      : 'Party package purchases are currently disabled'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleTogglePartyPackagePurchases}
                  disabled={isLoadingTogglePartyPackagePurchases}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.isPartyPackagePurchasesEnabled
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`}
                >
                  {isLoadingTogglePartyPackagePurchases ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mx-1"></div>
                  ) : (
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        settings.isPartyPackagePurchasesEnabled
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  )}
                </button>
              </div>
            </Card>
          )}

          {/* Enable Skating Lesson Purchases Section */}
          {hasExistingSettings && (
            <Card className="flex flex-col">
              <SectionHeader
                title="Enable Skating Lesson Purchases"
                icon={MdSchedule}
              />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Skating Lesson Purchase Status
                  </p>
                  <p className="text-xs text-gray-500">
                    {settings.isSkatingLessonPurchasesEnabled
                      ? 'Skating lesson purchases are currently enabled'
                      : 'Skating lesson purchases are currently disabled'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleSkatingLessonPurchases}
                  disabled={isLoadingToggleSkatingLessonPurchases}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.isSkatingLessonPurchasesEnabled
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`}
                >
                  {isLoadingToggleSkatingLessonPurchases ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mx-1"></div>
                  ) : (
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        settings.isSkatingLessonPurchasesEnabled
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  )}
                </button>
              </div>
            </Card>
          )}

          {/* Enable Hockey Lesson Purchases Section */}
          {hasExistingSettings && (
            <Card className="flex flex-col">
              <SectionHeader
                title="Enable Hockey Lesson Purchases"
                icon={MdSchedule}
              />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Hockey Lesson Purchase Status
                  </p>
                  <p className="text-xs text-gray-500">
                    {settings.isHockeyLessonPurchasesEnabled
                      ? 'Hockey lesson purchases are currently enabled'
                      : 'Hockey lesson purchases are currently disabled'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleHockeyLessonPurchases}
                  disabled={isLoadingToggleHockeyLessonPurchases}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.isHockeyLessonPurchasesEnabled
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`}
                >
                  {isLoadingToggleHockeyLessonPurchases ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mx-1"></div>
                  ) : (
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        settings.isHockeyLessonPurchasesEnabled
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  )}
                </button>
              </div>
            </Card>
          )}

          {/* Session Times Section */}
          {hasExistingSettings && (
            <Card className="flex flex-col">
              <SectionHeader
                title="Daily Session Times"
                icon={MdSchedule}
                showEditButton={true}
                isEditMode={isSessionEditMode}
                onEditToggle={handleSessionEditToggle}
              />

              <div className="space-y-4">
                {settings.sessionTimes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {isSessionEditMode
                      ? 'No session times configured. Add your first session below.'
                      : 'No session times configured.'}
                  </div>
                ) : (
                  settings.sessionTimes.map(
                    (session: SessionTimes, index: number) => (
                      <div
                        key={session.id}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-gray-900">
                            Session {index + 1}
                          </h3>
                          {isSessionEditMode &&
                            settings.sessionTimes.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSessionTime(session.id)}
                                className="text-red-600 hover:text-red-800 transition-colors duration-200"
                                disabled={removingSessionId === session.id}
                              >
                                {removingSessionId === session.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                ) : (
                                  <MdDelete className="text-lg" />
                                )}
                              </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Time
                            </label>
                            {isSessionEditMode ? (
                              <input
                                type="time"
                                value={session.startTime}
                                onChange={e =>
                                  handleSessionTimeChange(
                                    session.id,
                                    'startTime',
                                    e.target.value,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            ) : (
                              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900">
                                {HelperMethods.formatTime12Hour(
                                  session.startTime,
                                )}
                              </div>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Session duration: 1 hour
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <MdPeople className="mr-1 text-gray-500" />
                              Max Capacity
                            </label>
                            {isSessionEditMode ? (
                              <input
                                type="number"
                                min="1"
                                max="200"
                                value={session.maxCapacity}
                                onChange={e =>
                                  handleSessionTimeChange(
                                    session.id,
                                    'maxCapacity',
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            ) : (
                              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900">
                                {session.maxCapacity}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <MdCelebration className="mr-1 text-gray-500" />
                              Parties Limit
                            </label>
                            {isSessionEditMode ? (
                              <input
                                type="number"
                                min="1"
                                max="200"
                                value={session.partiesLimit}
                                onChange={e =>
                                  handleSessionTimeChange(
                                    session.id,
                                    'partiesLimit',
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            ) : (
                              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900">
                                {session.partiesLimit}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ),
                  )
                )}
              </div>

              {/* Add Session Button - only show in edit mode */}
              {isSessionEditMode && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={addSessionTime}
                    disabled={isLoadingAddSession}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isLoadingAddSession ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <MdAdd className="mr-1" />
                        Add Session
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Update Session Times Button */}
              {isSessionEditMode && (
                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={handleUpdateSessionTimes}
                    disabled={
                      isLoadingSessions || settings.sessionTimes.length === 0
                    }
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <MdSave className="mr-2" />
                    {isLoadingSessions
                      ? 'Updating...'
                      : 'Update Daily Session Times'}
                  </button>
                </div>
              )}
            </Card>
          )}
        </form>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default SeasonSettings;
